import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Downloads for the Ingredient Reconciliation registry.
 *
 * Both formats take the rows the page has already built — category, name, and the unit /
 * quantity strings exactly as the table renders them — so a file never disagrees with
 * the screen it was downloaded from. Nothing is fetched or recomputed here.
 *
 * rows: [{ category, name, unit, qty, duplicateOf, isSavedPreparation, isComplimentary }]
 *   duplicateOf        — names this row is a possible duplicate of ('' when none)
 *   isSavedPreparation — the row's yellow dot: a saved preparation
 *   isComplimentary    — named after one of the business's active complimentary items
 *                        (PDF only; the page does not mark these)
 * meta: { filterLabel } — what the list was narrowed to (tab / search), or '' for all.
 */

const TITLE = 'Ingredient Reconciliation';

function istStamp() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

function istGeneratedAt() {
  return new Date().toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fileName(ext, filterLabel) {
  const suffix = filterLabel ? `_${filterLabel.replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '')}` : '';
  return `Ingredient_Reconciliation${suffix}_${istStamp()}.${ext}`;
}

const duplicateNote = (r) => (r.duplicateOf ? `Possible duplicate of ${r.duplicateOf}` : '');

export function downloadReconciliationExcel(rows, { filterLabel = '' } = {}) {
  const sheetRows = rows.map((r) => ({
    Category: r.category,
    Ingredient: r.name,
    Unit: r.unit,
    Qty: r.qty,
    Notes: [r.isSavedPreparation ? 'Saved preparation' : '', duplicateNote(r)].filter(Boolean).join('; '),
  }));
  const ws = XLSX.utils.json_to_sheet(sheetRows, {
    header: ['Category', 'Ingredient', 'Unit', 'Qty', 'Notes'],
  });
  ws['!cols'] = [{ wch: 18 }, { wch: 36 }, { wch: 8 }, { wch: 14 }, { wch: 48 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Ingredients');
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([out], { type: 'application/octet-stream' }), fileName('xlsx', filterLabel));
}

const CATEGORY_BAND = [241, 245, 249];
const PREPARATION_BAND = [254, 243, 199];
const COMPLIMENTARY_BAND = [220, 252, 231];

/**
 * The PDF's sections, in print order: every ordinary ingredient under its category, then
 * all saved preparations, then all complimentary items — the last two pulled out of
 * whatever category they sit in on screen so they read as one block at the end.
 *
 * Each row appears exactly once. An ingredient that is both a saved preparation and a
 * complimentary item goes with the preparations and says so in its notes.
 *
 * Category sections keep the on-screen order. The two gathered sections have no screen
 * order to match — kept as-is they would read as one A–Z run per source category — so
 * they are sorted by name.
 */
const byName = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true });

function pdfSections(rows) {
  const byCategory = new Map();
  const preparations = [];
  const complimentary = [];

  rows.forEach((r) => {
    if (r.isSavedPreparation) {
      preparations.push({ ...r, notes: [duplicateNote(r), r.isComplimentary ? 'Also a complimentary item' : ''].filter(Boolean).join('; ') });
    } else if (r.isComplimentary) {
      complimentary.push({ ...r, notes: duplicateNote(r) });
    } else {
      if (!byCategory.has(r.category)) byCategory.set(r.category, []);
      byCategory.get(r.category).push({ ...r, notes: duplicateNote(r) });
    }
  });

  return [
    ...[...byCategory].map(([title, items]) => ({ title, items, fill: CATEGORY_BAND })),
    { title: 'Saved preparations', items: preparations.sort(byName), fill: PREPARATION_BAND },
    { title: 'Complimentary items', items: complimentary.sort(byName), fill: COMPLIMENTARY_BAND },
  ].filter((section) => section.items.length > 0);
}

export function downloadReconciliationPdf(rows, { filterLabel = '' } = {}) {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const margin = 14;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.text(TITLE, margin, 18);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(100, 116, 139);
  const summary = [
    `Generated ${istGeneratedAt()}`,
    `${rows.length} ingredient${rows.length === 1 ? '' : 's'}`,
    filterLabel ? `Filter: ${filterLabel}` : 'All categories',
  ].join('  ·  ');
  pdf.text(summary, margin, 24);
  pdf.setTextColor(0, 0, 0);

  // One table with a full-width label row per section, so the grouping survives page
  // breaks and the column header repeats on every page.
  const body = [];
  pdfSections(rows).forEach(({ title, items, fill }) => {
    body.push([{
      content: `${title}  ·  ${items.length} item${items.length === 1 ? '' : 's'}`,
      colSpan: 3,
      styles: { fontStyle: 'bold', fillColor: fill, textColor: [2, 8, 23] },
    }]);
    items.forEach((r) => body.push([r.name, r.unit, r.notes]));
  });

  autoTable(pdf, {
    startY: 30,
    head: [['Ingredient', 'Unit', 'Notes']],
    body,
    theme: 'grid',
    headStyles: { fillColor: [2, 8, 23], textColor: [248, 250, 252], fontStyle: 'bold' },
    styles: { fontSize: 9, cellPadding: 2.5, lineColor: [226, 232, 240], lineWidth: 0.1 },
    columnStyles: {
      0: { cellWidth: 82 },
      1: { cellWidth: 20 },
      2: { cellWidth: 'auto' },
    },
    margin: { left: margin, right: margin, bottom: 16 },
    didDrawPage: () => {
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pageWidth = pdf.internal.pageSize.getWidth();
      pdf.setFontSize(8);
      pdf.setTextColor(100, 116, 139);
      pdf.text(TITLE, margin, pageHeight - 8);
      pdf.text(`Page ${pdf.getNumberOfPages()}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
      pdf.setTextColor(0, 0, 0);
    },
  });

  pdf.save(fileName('pdf', filterLabel));
}
